import { FC } from "react";
import { useNavigate, useRouteError } from "react-router-dom";
import { Result, Button, Typography, Card } from "antd";
import { HomeOutlined, ReloadOutlined } from '@ant-design/icons';
import { isStaffAdminHost } from "@/adminHost";

const { Text } = Typography;

// Define an interface for error objects
interface ErrorWithMessage {
  message: string;
  stack?: string;
}

// Type guard to check if an object is an ErrorWithMessage
function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  );
}

// Function to convert unknown error to ErrorWithMessage
function toErrorWithMessage(error: unknown): ErrorWithMessage {
  if (isErrorWithMessage(error)) {
    return error;
  }

  // Handle null/undefined cases
  if (error === null || error === undefined) {
    return new Error('No error information available');
  }

  try {
    return new Error(String(error));
  } catch {
    // fallback in case there's an error during conversion
    return new Error('Unknown error');
  }
}

const ErrorPage: FC = () => {
  const navigate = useNavigate();
  const routeError = useRouteError();
  const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development';

  // Debug logging to understand when and why error page is rendered
  console.log('ErrorPage rendered with routeError:', routeError);
  console.log('Error type:', typeof routeError);
  console.log('Current location:', window.location.href);

  // If there's no route error, redirect to home instead of showing error page
  if (!routeError) {
    console.warn('ErrorPage rendered without a route error, redirecting to home');
    navigate(isStaffAdminHost() ? '/admin' : '/', { replace: true });
    return null;
  }

  // Convert to a properly typed error
  const error = toErrorWithMessage(routeError);

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      padding: '20px',
      background: '#f5f5f5'
    }}>
      <Card 
        style={{ 
          maxWidth: '800px', 
          width: '100%',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        <Result
          status="error"
          title="Something went wrong"
          subTitle="We're sorry, but we couldn't load the page you requested."
          extra={[
            <Button 
              type="primary" 
              key="reload" 
              icon={<ReloadOutlined />}
              onClick={() => window.location.reload()}
            >
              Reload Page
            </Button>,
            <Button 
              key="home" 
              icon={<HomeOutlined />}
              onClick={() => navigate(isStaffAdminHost() ? '/admin' : '/')}
            >
              Go to Home
            </Button>
          ]}
        />
        
        {isDevelopment && (
          <div style={{ marginTop: 20, padding: 16, borderTop: '1px solid #f0f0f0' }}>
            <Text strong>Error Details (only visible in development):</Text>
            <div 
              style={{ 
                marginTop: 10,
                padding: 16, 
                background: '#f9f9f9', 
                borderRadius: 4,
                border: '1px solid #e8e8e8',
                overflow: 'auto'
              }}
            >
              <Text type="danger">{error.message}</Text>
              {error.stack && (
                <div style={{ marginTop: 10 }}>
                  <Text type="secondary" style={{ whiteSpace: 'pre-wrap' }}>
                    {error.stack}
                  </Text>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ErrorPage; 